import {path, propOr, curry, anyPass} from 'ramda'

import {
  PRESENTER_TYPES,
  FETCH_ROOMS_PRESENTER_ERRORS,
  BOOKING_PRESENTER_ERRORS,
  PresenterTypesType
} from './constants'

import {
  getModuleState as getRoomModuleState,
  getRooms
} from 'modules/rooms/selectors'
import {getModuleState as getOfferCheckModuleState} from 'modules/offerCheck/selectors'

import {offerCheckError} from 'modules/offerCheck/slice'

import {StoreStateType} from 'modules'
import {ErrorResponse} from '@daedalus/core/src/booking/types/ErrorResponse'
import {ERROR} from './actions/error'
import {BOOK_OFFER_FINALIZATION_ERROR} from 'modules/book/actions/bookOfferFinalization'
import {BOOK_OFFER_PSD2_ERROR} from 'modules/book/actions/bookOfferPsd2'
import {bookOfferCreationError} from 'modules/book/slice'
import {roomsFetchDone, roomsFetchError} from 'modules/rooms/slice'
import {toggle} from 'opticks/lib/optimizely'
import {getIsWrongCCError} from 'components/CheckoutForm/PaymentErrorMessages/WrongCCNumber'
import {getIsWrongCVCError} from 'components/CheckoutForm/PaymentErrorMessages/WrongCVCCode'
import {getIsGenericPaymentDeclined} from 'components/CheckoutForm/PaymentErrorMessages/GenericPaymentDeclined'
import {getIsGenericFraudDeclined} from 'components/CheckoutForm/PaymentErrorMessages/GenericFraudDeclined'
import {getIsPaymentNotAuthorized} from 'components/CheckoutForm/PaymentErrorMessages/PaymentNotAuthorized'

type ErrorType = {
  errorResponse: ErrorResponse
  originalErrorAction: string
}

export const getModuleState = (state: StoreStateType) =>
  state?.common?.moduleState

export const didSoldOutErrorHappen = (state: StoreStateType) => {
  if (getOfferCheckModuleState(state) === offerCheckError.type) {
    return (
      state?.common?.error?.errorResponse?.type === 'offer_expired' ||
      state?.common?.error?.errorResponse?.details?.category === 'sold_out'
    )
  }

  if (getModuleState(state) === ERROR) {
    return state?.common?.error?.errorResponse?.details?.category === 'sold_out'
  }

  if (getRoomModuleState(state) === roomsFetchDone.type) {
    return getRooms(state)?.length === 0
  }

  return state?.common?.error?.errorResponse?.details?.category === 'sold_out'
}

const testNewArchitectureErrors = anyPass([
  getIsWrongCVCError,
  getIsWrongCCError,
  getIsGenericPaymentDeclined,
  getIsGenericFraudDeclined,
  getIsPaymentNotAuthorized
])

export const didPaymentErrorHappen = (state: StoreStateType) => {
  const BOOKING_ERRORS = [
    BOOK_OFFER_FINALIZATION_ERROR,
    BOOK_OFFER_PSD2_ERROR,
    bookOfferCreationError.type
  ]

  const notSoldOutError = !didSoldOutErrorHappen(state)
  const originalError = getError(state)

  const newBookingEndpoints = toggle('eeu0jio-booking-flow-errors', false, true)

  /**
   * Bypass to show the popup error when the wrong CC error happens
   */

  if (
    newBookingEndpoints &&
    testNewArchitectureErrors(originalError?.errorResponse)
  ) {
    return false
  }

  return (
    notSoldOutError &&
    BOOKING_ERRORS.includes(originalError?.originalErrorAction)
  )
}

export const getOriginError = path(['originalError'])

export const getError = path(['common', 'error'])

export const getErrorByAction = (action: string) => {
  if (action === offerCheckError.type || action === roomsFetchError.type) {
    return FETCH_ROOMS_PRESENTER_ERRORS
  }

  return BOOKING_PRESENTER_ERRORS
}

const getPresenterID = (error: ErrorType) => {
  switch (error.errorResponse.type) {
    case 'provider':
      return propOr(
        PRESENTER_TYPES.GenericError,
        error.errorResponse.details.category,
        getErrorByAction(error.originalErrorAction)
      )

    case 'booking_unauthorized':
      return propOr(
        PRESENTER_TYPES.UnauthorizedBookingError,
        error.errorResponse.details.category,
        getErrorByAction(error.originalErrorAction)
      )

    case 'input_validation':
      return propOr(
        PRESENTER_TYPES.GenericError,
        error.errorResponse.type,
        getErrorByAction(error.originalErrorAction)
      )

    case 'pci_proxy':
      return PRESENTER_TYPES.PopUpPaymentError

    case 'fatal':
      return PRESENTER_TYPES.GenericError

    default:
      return propOr(
        PRESENTER_TYPES.GenericError,
        error.errorResponse.details.category,
        getErrorByAction(error.originalErrorAction)
      )
  }
}

const hasBackendResponse = state =>
  Boolean(path(['common', 'error', 'errorResponse'], state))

// @Todo - Refactor it after website-2 since now on B side we have
// just one presenter for all Overlay Errors
export const getErrorByPresenterType = curry(
  (presenter: PresenterTypesType, state: StoreStateType) => {
    const newBookingEndpoints = toggle(
      'eeu0jio-booking-flow-errors',
      false,
      true
    )

    if (hasBackendResponse(state)) {
      /**
       * Bypass to show the popup error when the wrong CC error happens
       */
      if (
        newBookingEndpoints &&
        testNewArchitectureErrors(state.common.error?.errorResponse)
      ) {
        return undefined
      }

      const currentPresenter = getPresenterID(state.common.error)
      if (currentPresenter === presenter) {
        return state.common.error
      }
    }
  }
)

export const getHttpRequestId = state => state.common?.httpRequestId

export const getIsSearchBoxVisible = (state: StoreStateType) =>
  state.common.isSearchBoxVisible
